import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Mail, Globe, Clock, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Container } from '@/components/layout/Container';
import { Section, SectionHeading } from '@/components/layout/Section';
import { Reveal } from '@/components/layout/Reveal';
import { CtaButton } from '@/components/site/CtaButton';
import { WhatsAppButton, WhatsAppGlyph } from '@/components/site/WhatsAppButton';
import { PhotoFrame } from '@/components/site/PhotoFrame';
import { AGENCY, IMAGES, whatsappLink } from '@/lib/site';
import { useI18n, interpolate } from '@/lib/i18n';
import { createLead } from '@/lib/api';

const EMPTY = {
  name: '',
  brand: '',
  email: '',
  service_interest: '',
  budget: '',
  timeline: '',
  challenge: '',
};

export default function Contact() {
  const { t, lang } = useI18n();
  const p = t.contactPage;

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const formRef = useRef(null);

  // Deep link support: /contact#consultation-form
  useEffect(() => {
    if (window.location.hash === '#consultation-form') {
      const timer = setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 220);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const setField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Budget and timeline only apply to build projects — drop them again if
      // the visitor switches back to a marketing service.
      if (key === 'service_interest' && !t.techProjectValues.includes(value)) {
        next.budget = '';
        next.timeline = '';
      }
      return next;
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /** True when the selected interest is a website / app / automation project. */
  const isProjectEnquiry = t.techProjectValues.includes(form.service_interest);

  /** Grouped options for the interest dropdown (marketing vs. build). */
  const optionGroups = React.useMemo(() => {
    const order = ['marketing', 'tech', 'other'];
    return order
      .map((key) => ({
        key,
        label: t.serviceOptionGroups[key],
        options: t.serviceOptions.filter((o) => (o.group || 'other') === key),
      }))
      .filter((g) => g.options.length > 0);
  }, [t]);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = p.form.errors.name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = p.form.errors.email;
    if (!form.challenge.trim() || form.challenge.trim().length < 10)
      next.challenge = p.form.errors.challenge;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(p.form.errors.incomplete);
      return;
    }
    setSubmitting(true);
    try {
      await createLead({
        name: form.name.trim(),
        brand: form.brand.trim() || null,
        email: form.email.trim(),
        service_interest: form.service_interest || null,
        budget: form.budget || null,
        timeline: form.timeline || null,
        challenge: form.challenge.trim(),
        lang,
      });
      setSubmitted({ name: form.name.trim(), email: form.email.trim() });
      setForm(EMPTY);
      toast.success(p.form.toastSuccess);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : p.form.errors.failed);
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    'h-12 w-full rounded-[12px] border px-4 text-[0.92rem] text-[color:var(--rmh-ink)] outline-none transition-colors duration-300 placeholder:text-[color:var(--rmh-muted-2)] focus:border-[color:var(--rmh-accent)]';

  const selectChevron =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236B625B' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E\")";

  const Err = ({ name }) =>
    errors[name] ? (
      <p
        className="mt-2 flex items-center gap-1.5 text-[0.76rem]"
        style={{ color: 'var(--rmh-mocha)' }}
        data-testid={`consultation-form-${name}-error`}
      >
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {errors[name]}
      </p>
    ) : null;

  return (
    <>
      {/* -------------------------------- hero --------------------------------- */}
      <section
        data-testid="contact-hero"
        className="relative overflow-hidden border-b border-[color:var(--rmh-border)]"
        style={{ backgroundColor: 'var(--rmh-canvas)' }}
      >
        <span
          aria-hidden="true"
          className="rmh-champagne-wash pointer-events-none absolute inset-0"
        />
        <Container wide className="relative z-10 py-14 lg:py-20">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="flex items-center gap-3">
                  <span
                    className="block h-px w-10"
                    style={{ backgroundColor: 'var(--rmh-accent)' }}
                    aria-hidden="true"
                  />
                  <span className="rmh-eyebrow">{p.hero.eyebrow}</span>
                </div>
                <h1
                  data-testid="contact-hero-title"
                  className="rmh-display mt-7 text-[2.2rem] sm:text-[2.9rem] lg:text-[3.7rem]"
                >
                  {p.hero.titleLines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </h1>
                <p className="rmh-body mt-7 max-w-xl text-[0.98rem] sm:text-[1.04rem]">
                  {p.hero.lede}
                </p>

                <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <CtaButton
                    href="#consultation-form"
                    size="lg"
                    variant="primary"
                    arrow
                    testId="contact-hero-scroll-to-form-button"
                    className="w-full sm:w-auto"
                  >
                    {t.common.bookConsultation}
                  </CtaButton>
                  <WhatsAppButton
                    variant="outline"
                    size="lg"
                    long
                    testId="contact-hero-whatsapp-link"
                    className="w-full sm:w-auto"
                  />
                </div>

                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <a
                    href={`mailto:${AGENCY.email}`}
                    data-testid="contact-email-link"
                    className="group rounded-[14px] border p-5 transition-colors duration-300 hover:border-[color:var(--rmh-ink)]"
                    style={{
                      backgroundColor: 'var(--rmh-surface)',
                      borderColor: 'var(--rmh-border)',
                    }}
                  >
                    <Mail
                      className="h-4 w-4"
                      strokeWidth={1.5}
                      style={{ color: 'var(--rmh-accent-2)' }}
                    />
                    <p className="rmh-eyebrow mt-4 text-[0.55rem]">
                      {p.hero.emailLabel}
                    </p>
                    <p className="mt-1.5 break-all text-[0.8rem] text-[color:var(--rmh-ink)]">
                      {AGENCY.email}
                    </p>
                  </a>

                  <a
                    href={whatsappLink(t.whatsappMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-card-link"
                    className="group rounded-[14px] border p-5 transition-colors duration-300 hover:border-[color:var(--rmh-ink)]"
                    style={{
                      backgroundColor: 'var(--rmh-surface)',
                      borderColor: 'var(--rmh-border)',
                    }}
                  >
                    <WhatsAppGlyph className="h-4 w-4" />
                    <p className="rmh-eyebrow mt-4 text-[0.55rem]">
                      {p.hero.whatsappLabel}
                    </p>
                    <p className="mt-1.5 text-[0.8rem] text-[color:var(--rmh-ink)]">
                      {AGENCY.whatsappNumber}
                    </p>
                  </a>

                  <div
                    className="rounded-[14px] border p-5"
                    style={{
                      backgroundColor: 'var(--rmh-surface)',
                      borderColor: 'var(--rmh-border)',
                    }}
                    data-testid="contact-response-time-card"
                  >
                    <Clock
                      className="h-4 w-4"
                      strokeWidth={1.5}
                      style={{ color: 'var(--rmh-accent-2)' }}
                    />
                    <p className="rmh-eyebrow mt-4 text-[0.55rem]">
                      {p.hero.responseLabel}
                    </p>
                    <p className="mt-1.5 text-[0.8rem] text-[color:var(--rmh-ink)]">
                      {p.hero.responseValue}
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.12} className="lg:col-span-5">
              <div className="mx-auto max-w-[400px] lg:max-w-none">
                <PhotoFrame
                  src={IMAGES.authorityPortrait}
                  webp={IMAGES.authorityPortraitWebp}
                  alt={t.photo.contactHeroAlt}
                  ratio="4 / 5"
                  objectPosition="50% 28%"
                  caption={t.photo.contactHeroCaption}
                  eager
                />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* -------------------------------- form ---------------------------------- */}
      <Section tone="canvas" wide id="consultation-form" className="scroll-mt-24">
        <div ref={formRef} className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                eyebrow={p.form.eyebrow}
                title={p.form.title}
                lede={p.form.lede}
              />
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 flex flex-col gap-6">
                {p.faq.map((item, i) => (
                  <div
                    key={item.q}
                    data-testid={`contact-faq-${i}`}
                    className="border-t pt-5"
                    style={{ borderColor: 'var(--rmh-border)' }}
                  >
                    <p className="font-serif text-[1.02rem] text-[color:var(--rmh-ink)]">
                      {item.q}
                    </p>
                    <p className="rmh-body mt-2.5 text-[0.875rem]">{item.a}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.06}>
              {submitted ? (
                <div
                  data-testid="consultation-form-success"
                  className="rounded-[18px] border p-8 sm:p-10"
                  style={{
                    backgroundColor: 'var(--rmh-surface)',
                    borderColor: 'var(--rmh-border-strong)',
                    boxShadow: 'var(--shadow-soft)',
                  }}
                >
                  <span
                    className="inline-flex h-12 w-12 items-center justify-center rounded-full border"
                    style={{
                      backgroundColor: 'var(--rmh-surface-inset)',
                      borderColor: 'var(--rmh-border)',
                    }}
                  >
                    <Check
                      className="h-5 w-5"
                      strokeWidth={1.6}
                      style={{ color: 'var(--rmh-accent-2)' }}
                    />
                  </span>
                  <h2 className="rmh-h2 mt-7 text-[1.6rem] sm:text-[1.9rem]">
                    {interpolate(p.success.title, {
                      name: submitted.name.split(' ')[0],
                    })}
                  </h2>
                  <p className="rmh-body mt-4 text-[0.95rem]">
                    {interpolate(p.success.body, { email: submitted.email })}
                  </p>

                  <div className="rmh-rule mt-8" />
                  <p className="rmh-eyebrow mt-6 text-[0.56rem]">
                    {p.success.whileYouWait}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <WhatsAppButton
                      variant="solid"
                      size="md"
                      label={p.success.continueWhatsapp}
                      testId="consultation-success-whatsapp-link"
                    />
                    <CtaButton
                      to="/services"
                      variant="secondary"
                      size="md"
                      testId="consultation-success-services-button"
                    >
                      {t.common.exploreTheServices}
                    </CtaButton>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubmitted(null)}
                    data-testid="consultation-form-send-another-button"
                    className="rmh-link-underline mt-8 inline-flex items-center gap-1.5 text-[0.78rem] uppercase tracking-[0.14em] text-[color:var(--rmh-muted)]"
                  >
                    {p.success.sendAnother}
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  noValidate
                  data-testid="consultation-form"
                  className="rounded-[18px] border p-6 sm:p-9"
                  style={{
                    backgroundColor: 'var(--rmh-surface)',
                    borderColor: 'var(--rmh-border)',
                    boxShadow: 'var(--shadow-soft)',
                  }}
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="rmh-eyebrow block text-[0.56rem]"
                      >
                        {p.form.nameLabel}
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        value={form.name}
                        onChange={setField('name')}
                        placeholder={p.form.namePlaceholder}
                        autoComplete="name"
                        aria-invalid={Boolean(errors.name)}
                        data-testid="consultation-form-name-input"
                        className={`${inputBase} mt-3`}
                        style={{
                          backgroundColor: 'var(--rmh-surface-inset)',
                          borderColor: errors.name
                            ? 'var(--rmh-mocha)'
                            : 'var(--rmh-border)',
                        }}
                      />
                      <Err name="name" />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-brand"
                        className="rmh-eyebrow block text-[0.56rem]"
                      >
                        {p.form.brandLabel}
                      </label>
                      <input
                        id="contact-brand"
                        type="text"
                        value={form.brand}
                        onChange={setField('brand')}
                        placeholder={p.form.brandPlaceholder}
                        autoComplete="organization"
                        data-testid="consultation-form-brand-input"
                        className={`${inputBase} mt-3`}
                        style={{
                          backgroundColor: 'var(--rmh-surface-inset)',
                          borderColor: 'var(--rmh-border)',
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="rmh-eyebrow block text-[0.56rem]"
                      >
                        {p.form.emailLabel}
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        value={form.email}
                        onChange={setField('email')}
                        placeholder={p.form.emailPlaceholder}
                        autoComplete="email"
                        aria-invalid={Boolean(errors.email)}
                        data-testid="consultation-form-email-input"
                        className={`${inputBase} mt-3`}
                        style={{
                          backgroundColor: 'var(--rmh-surface-inset)',
                          borderColor: errors.email
                            ? 'var(--rmh-mocha)'
                            : 'var(--rmh-border)',
                        }}
                      />
                      <Err name="email" />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-service"
                        className="rmh-eyebrow block text-[0.56rem]"
                      >
                        {p.form.serviceLabel}
                      </label>
                      <select
                        id="contact-service"
                        value={form.service_interest}
                        onChange={setField('service_interest')}
                        data-testid="consultation-form-service-select"
                        className={`${inputBase} mt-3 appearance-none pr-10`}
                        style={{
                          backgroundColor: 'var(--rmh-surface-inset)',
                          borderColor: 'var(--rmh-border)',
                          backgroundImage: selectChevron,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 1rem center',
                        }}
                      >
                        <option value="">{p.form.servicePlaceholder}</option>
                        {optionGroups.map((group) => (
                          <optgroup key={group.key} label={group.label}>
                            {group.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Build projects need scope before a useful answer is possible. */}
                  {isProjectEnquiry ? (
                    <div
                      data-testid="consultation-form-project-fields"
                      className="mt-5 rounded-[14px] border p-5"
                      style={{
                        backgroundColor: 'var(--rmh-canvas-2)',
                        borderColor: 'var(--rmh-border)',
                      }}
                    >
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor="contact-budget"
                            className="rmh-eyebrow block text-[0.56rem]"
                          >
                            {p.form.budgetLabel}
                          </label>
                          <select
                            id="contact-budget"
                            value={form.budget}
                            onChange={setField('budget')}
                            data-testid="consultation-form-budget-select"
                            className={`${inputBase} mt-3 appearance-none pr-10`}
                            style={{
                              backgroundColor: 'var(--rmh-surface-inset)',
                              borderColor: 'var(--rmh-border)',
                              backgroundImage: selectChevron,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 1rem center',
                            }}
                          >
                            <option value="">{p.form.budgetPlaceholder}</option>
                            {t.budgetOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label
                            htmlFor="contact-timeline"
                            className="rmh-eyebrow block text-[0.56rem]"
                          >
                            {p.form.timelineLabel}
                          </label>
                          <select
                            id="contact-timeline"
                            value={form.timeline}
                            onChange={setField('timeline')}
                            data-testid="consultation-form-timeline-select"
                            className={`${inputBase} mt-3 appearance-none pr-10`}
                            style={{
                              backgroundColor: 'var(--rmh-surface-inset)',
                              borderColor: 'var(--rmh-border)',
                              backgroundImage: selectChevron,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 1rem center',
                            }}
                          >
                            <option value="">
                              {p.form.timelinePlaceholder}
                            </option>
                            {t.timelineOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <p className="rmh-body mt-4 text-[0.79rem] leading-[1.55]">
                        {p.form.projectHint}
                      </p>
                    </div>
                  ) : null}

                  <div className="mt-5">
                    <label
                      htmlFor="contact-challenge"
                      className="rmh-eyebrow block text-[0.56rem]"
                    >
                      {p.form.challengeLabel}
                    </label>
                    <textarea
                      id="contact-challenge"
                      rows={5}
                      value={form.challenge}
                      onChange={setField('challenge')}
                      placeholder={p.form.challengePlaceholder}
                      aria-invalid={Boolean(errors.challenge)}
                      data-testid="consultation-form-challenge-input"
                      className="mt-3 w-full resize-none rounded-[12px] border px-4 py-3.5 text-[0.92rem] leading-relaxed text-[color:var(--rmh-ink)] outline-none transition-colors duration-300 placeholder:text-[color:var(--rmh-muted-2)] focus:border-[color:var(--rmh-accent)]"
                      style={{
                        backgroundColor: 'var(--rmh-surface-inset)',
                        borderColor: errors.challenge
                          ? 'var(--rmh-mocha)'
                          : 'var(--rmh-border)',
                      }}
                    />
                    <Err name="challenge" />
                  </div>

                  <div className="rmh-rule mt-8" />

                  <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[0.74rem] leading-relaxed text-[color:var(--rmh-muted)]">
                      {p.form.privacy}
                    </p>
                    <CtaButton
                      type="submit"
                      size="lg"
                      variant="primary"
                      arrow
                      disabled={submitting}
                      testId="consultation-form-submit-button"
                      className="w-full sm:w-auto"
                    >
                      {submitting ? t.common.sending : p.form.submit}
                    </CtaButton>
                  </div>
                </form>
              )}
            </Reveal>

            <Reveal delay={0.14}>
              <div
                className="mt-6 flex flex-col gap-4 rounded-[16px] border p-6 sm:flex-row sm:items-center sm:justify-between"
                style={{
                  backgroundColor: 'var(--rmh-canvas-2)',
                  borderColor: 'var(--rmh-border)',
                }}
              >
                <div>
                  <p className="font-serif text-[1.05rem] text-[color:var(--rmh-ink)]">
                    {p.aiTeaser.title}
                  </p>
                  <p className="rmh-body mt-2 text-[0.86rem]">{p.aiTeaser.body}</p>
                </div>
                <a
                  href={AGENCY.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-website-link"
                  className="inline-flex shrink-0 items-center gap-2 text-[0.8rem] text-[color:var(--rmh-muted)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {AGENCY.website}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section tone="canvas2" wide className="py-12 lg:py-16">
        <Reveal>
          <p className="rmh-body text-center text-[0.9rem]">
            {p.bottom.prefix}{' '}
            <Link
              to="/services"
              data-testid="contact-bottom-services-link"
              className="rmh-link-underline text-[color:var(--rmh-ink)]"
            >
              {p.bottom.servicesLink}
            </Link>{' '}
            {t.common.or}{' '}
            <Link
              to="/about"
              data-testid="contact-bottom-about-link"
              className="rmh-link-underline text-[color:var(--rmh-ink)]"
            >
              {p.bottom.aboutLink}
            </Link>
            .
          </p>
        </Reveal>
      </Section>
    </>
  );
}
